/*
  age-gate.js — LusoGourmet wine/spirits age verification (ADR-0004)
  Custom, app-free. The overlay is rendered server-side but hidden; this
  module shows it only on restricted (alcohol) pages when the visitor hasn't
  confirmed. Choice persists in localStorage (per-device). Accessible:
  role=dialog, focus trap, ESC = leave, scroll lock, focus restore.
  SEO: the page is fully server-rendered underneath; crawlers (no JS) are not
  blocked. No layout shift (overlay is position:fixed).
*/
const STORAGE_KEY = 'lusogourmet:age-confirmed';

class AgeGate extends HTMLElement {
  connectedCallback() {
    // Don't block editing in the theme editor.
    if (window.Shopify && window.Shopify.designMode) return;

    this.minAge = this.getAttribute('data-min-age') || '18';
    this.exitUrl = this.getAttribute('data-exit-url') || '/';

    let confirmed = false;
    try {
      confirmed = localStorage.getItem(STORAGE_KEY) === this.minAge;
    } catch (e) {
      /* storage blocked — fail open is unacceptable for a gate, so show it */
    }
    if (confirmed) {
      this.remove();
      return;
    }
    this.open();
  }

  open() {
    this.hidden = false;
    document.documentElement.classList.add('age-gate-open');
    this.previouslyFocused = document.activeElement;

    this.dialog = this.querySelector('[role="dialog"]');
    this.confirmBtn = this.querySelector('[data-age-confirm]');
    this.denyBtn = this.querySelector('[data-age-deny]');

    this.confirmBtn.addEventListener('click', () => this.confirm());
    this.denyBtn.addEventListener('click', () => this.deny());
    this.keyHandler = (e) => this.onKeydown(e);
    this.addEventListener('keydown', this.keyHandler);

    // Move focus into the dialog.
    window.requestAnimationFrame(() => this.confirmBtn.focus());
  }

  confirm() {
    try {
      localStorage.setItem(STORAGE_KEY, this.minAge);
    } catch (e) {
      /* ignore */
    }
    document.documentElement.classList.remove('age-gate-open');
    const restore = this.previouslyFocused;
    this.removeEventListener('keydown', this.keyHandler);
    this.remove();
    const target = restore && restore.focus ? restore : document.getElementById('MainContent');
    if (target && target.focus) target.focus();
  }

  deny() {
    // Leaving the restricted content is the keyboard-accessible way out.
    window.location.href = this.exitUrl;
  }

  onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.deny();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      this.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => !el.disabled && el.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

customElements.define('age-gate', AgeGate);
